import {
  BarChart3,
  Bell,
  ChefHat,
  CircleDollarSign,
  Clock3,
  Eye,
  PackageCheck,
  Plus,
  QrCode,
  RefreshCw,
  ShoppingBag,
  Store,
  Tags,
  Users,
  UtensilsCrossed,
} from 'lucide-react';

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Link,
  useNavigate,
} from 'react-router-dom';

import ActionCard from '../components/dashboard/ActionCard.jsx';

import RecentActivity from '../components/dashboard/RecentActivity.jsx';

import StatCard from '../components/dashboard/StatCard.jsx';

import dashboardApi from '../api/dashboardApi';

import { useAuth } from '../contexts/AuthContext';

import { useLanguage } from '../contexts/LanguageContext';

const ESTABLISHMENT_STORAGE_KEY =
  'selected_establishment_id';

const formatNumber = (
  value,
  language,
) => {

  const number =
    Number(value || 0);

  try {
    return new Intl.NumberFormat(
      language,
    ).format(number);

  } catch {
    return String(number);

  }

};

const formatMoney = (
  value,
  currency,
  language,
) => {

  const number =
    Number(value || 0);

  try {
    return new Intl.NumberFormat(
      language,
      {
        style: 'currency',
        currency:
          currency || 'MAD',
        maximumFractionDigits: 2,
      },
    ).format(number);

  } catch {
    return `${number.toFixed(2)} ${currency || 'MAD'}`;

  }

};

const normalizeTrend = (
  value,
) => {

  const number =
    Number(value);

  if (!Number.isFinite(number)) {
    return null;

  }

  return {
    label:
      `${number >= 0 ? '+' : ''}${number.toFixed(1)}%`,
    direction:
      number >= 0
        ? 'up'
        : 'down',
  };

};

const normalizeRole = (
  value,
) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');

const isSuperAdminRole = (
  value,
) => {

  const role =
    normalizeRole(value);

  return [
    'superadmin',
    'superadministrator',
  ].includes(role);

};

function DashboardLineChart({
  points = [],

  label,
  language,
  direction = 'ltr',
  averageLabel,
  peakLabel,
}) {

  const isRtl =
    direction === 'rtl';

  const chartPoints =
    Array.isArray(points)
      ? points
      : [];

  const values =
    chartPoints.map(
      (point) =>
        Number(
          point?.value || 0,
        ),
    );

  if (!chartPoints.length) {

    return (
      <div
        className="dashboard-chart dashboard-chart--empty"
        dir={direction}

      >
        <BarChart3 size={30} />
        <span>{label}</span>
      </div>
    );

  }

  const width = 760;

  const height = 280;

  const padding = {
    top: 26,
    right: 24,
    bottom: 48,
    left: 52,
  };

  const chartWidth =
    width -
    padding.left -
    padding.right;

  const chartHeight =
    height -
    padding.top -
    padding.bottom;

  const rawMax =
    Math.max(
      ...values,
      1,
    );

  const rawMin =
    Math.min(
      ...values,
      0,
    );

  const range =
    Math.max(
      rawMax -
        rawMin,
      1,
    );

  const extraTop =
    Math.max(
      range * 0.12,
      1,
    );

  const maxValue =
    rawMax +
    extraTop;

  const minValue =
    Math.max(
      0,
      rawMin -
        range * 0.05,
    );

  const visibleRange =
    Math.max(
      maxValue -
        minValue,
      1,
    );

  const coords =
    chartPoints.map(
      (
        point,
        index,
      ) => {

        const ratio =
          chartPoints.length <=
          1
            ? 0.5
            : index /
              (chartPoints.length -
                1);

        const x =
          chartPoints.length <=
          1
            ? padding.left +
              chartWidth / 2
            : isRtl
              ? width -
                padding.right -
                ratio *
                  chartWidth
              : padding.left +
                ratio *
                  chartWidth;

        const normalized =
          (Number(
            point?.value || 0,
          ) -
            minValue) /
          visibleRange;

        const y =
          padding.top +
          chartHeight -
          normalized *
            chartHeight;

        return {
          ...point,
          x,
          y,
          value: Number(
            point?.value || 0,
          ),
        };

      },
    );

  const buildSmoothPath =
    (items) => {

      if (!items.length) {
        return '';

      }

      if (
        items.length === 1
      ) {
        return `M ${items[0].x} ${items[0].y}`;

      }

      let path =
        `M ${items[0].x} ${items[0].y}`;

      for (
        let index = 0;

        index <
        items.length - 1;

        index += 1
      ) {

        const current =
          items[index];

        const next =
          items[index + 1];

        const middleX =
          (current.x +
            next.x) /
          2;

        path +=
          ` C ${middleX} ${current.y}, ${middleX} ${next.y}, ${next.x} ${next.y}`;

      }

      return path;

    };

  const linePath =
    buildSmoothPath(
      coords,
    );

  const baselineY =
    padding.top +
    chartHeight;

  const areaPath =
    coords.length
      ? `${linePath} L ${
          coords[
            coords.length - 1
          ].x
        } ${baselineY} L ${
          coords[0].x
        } ${baselineY} Z`
      : '';

  const peakPoint =
    coords.reduce(
      (
        best,
        current,
      ) =>
        current.value >
        best.value
          ? current
          : best,
      coords[0],

    );

  const latestPoint =
    coords[
      coords.length - 1
    ];

  const yTicks =
    Array.from(
      {
        length: 5,
      },
      (
        _,
        index,
      ) => {

        const ratio =
          index / 4;

        const value =
          maxValue -
          visibleRange *
            ratio;

        const y =
          padding.top +
          chartHeight *
            ratio;

        return {
          y,
          value:
            Math.max(
              0,
              Math.round(
                value,
              ),
            ),
        };

      },
    );

  const xLabelIndexes =
    Array.from(
      new Set([
        0,
        Math.floor(
          (chartPoints.length -
            1) /
            3,
        ),
        Math.floor(
          ((chartPoints.length -
            1) *
            2) /
            3,
        ),
        chartPoints.length -
          1,
      ]),
    ).filter(
      (index) =>
        index >= 0 &&
        index <
          chartPoints.length,
    );

  const formatDateLabel =
    (value) => {

      if (!value) {
        return '';

      }

      try {
        return new Intl.DateTimeFormat(
          language,
          {
            month: 'short',
            day: 'numeric',
          },
        ).format(
          new Date(value),
        );

      } catch {
        return value;

      }

    };

  return (
    <div
      className="dashboard-chart dashboard-chart--premium"
      dir={direction}

    >
      <div className="dashboard-chart__summary">
        <div>
          <span className="dashboard-chart__summary-label">
            {label}

          </span>
          <strong>
            {formatNumber(
              values.reduce(
                (
                  total,
                  value,
                ) =>
                  total +
                  value,
                0,
              ),
              language,
            )}

          </strong>
        </div>
        <div className="dashboard-chart__summary-metrics">
          <span>
            <em>
              {averageLabel}

            </em>
            <small>
              {formatNumber(
                Math.round(
                  values.reduce(
                    (
                      total,
                      value,
                    ) =>
                      total +
                      value,
                    0,
                  ) /
                    Math.max(
                      values.length,
                      1,
                    ),
                ),
                language,
              )}

            </small>
          </span>
          <span>
            <em>
              {peakLabel}

            </em>
            <small>
              {formatNumber(
                peakPoint.value,
                language,
              )}

            </small>
          </span>
        </div>
      </div>
      <div className="dashboard-chart__canvas dashboard-chart__canvas--premium">
        <svg
          viewBox={`0 0 ${width} ${height}`}

          role="img"
          aria-label={label}

          preserveAspectRatio="none"
          direction={direction}

        >
          <defs>
            <linearGradient
              id="dashboard-chart-area"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor="currentColor"
                stopOpacity="0.2"
              />
              <stop
                offset="100%"
                stopColor="currentColor"
                stopOpacity="0.015"
              />
            </linearGradient>
            <filter
              id="dashboard-chart-shadow"
              x="-20%"
              y="-20%"
              width="140%"
              height="140%"
            >
              <feDropShadow
                dx="0"
                dy="5"
                stdDeviation="5"
                floodOpacity="0.14"
              />
            </filter>
          </defs>
          {yTicks.map(
            (tick) => (
              <g
                key={`y-${tick.y}`}

              >
                <line
                  x1={
                    padding.left
                  }

                  y1={tick.y}

                  x2={
                    width -
                    padding.right
                  }

                  y2={tick.y}

                  className="dashboard-chart__grid-line"
                />
                <text
                  x={
                    isRtl
                      ? width -
                        padding.right +
                        12
                      : padding.left -
                        12
                  }

                  y={
                    tick.y +
                    4
                  }

                  textAnchor={
                    isRtl
                      ? 'start'
                      : 'end'
                  }

                  className="dashboard-chart__axis-label"
                >
                  {
                    tick.value
                  }

                </text>
              </g>
            ),
          )}

          {xLabelIndexes.map(
            (index) => {

              const point =
                coords[index];

              return (
                <line
                  key={`x-grid-${index}`}

                  x1={point.x}

                  y1={
                    padding.top
                  }

                  x2={point.x}

                  y2={
                    baselineY
                  }

                  className="dashboard-chart__grid-line dashboard-chart__grid-line--vertical"
                />
              );

            },
          )}

          <path
            d={areaPath}

            fill="url(#dashboard-chart-area)"
            className="dashboard-chart__area"
          />
          <path
            d={linePath}

            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="dashboard-chart__line"
            filter="url(#dashboard-chart-shadow)"
          />
          {coords.map(
            (
              point,
              index,
            ) => (
              <g
                key={
                  point.date ||
                  index
                }

              >
                <circle
                  cx={point.x}

                  cy={point.y}

                  r="7"
                  className="dashboard-chart__point-halo"
                />
                <circle
                  cx={point.x}

                  cy={point.y}

                  r="4"
                  className="dashboard-chart__point"
                />
              </g>
            ),
          )}

          <circle
            cx={
              peakPoint.x
            }

            cy={
              peakPoint.y
            }

            r="10"
            className="dashboard-chart__peak-ring"
          />
          <circle
            cx={
              latestPoint.x
            }

            cy={
              latestPoint.y
            }

            r="8"
            className="dashboard-chart__latest-ring"
          />
          <circle
            cx={
              latestPoint.x
            }

            cy={
              latestPoint.y
            }

            r="4.5"
            className="dashboard-chart__latest-point"
          />
          {xLabelIndexes.map(
            (index) => {

              const point =
                coords[index];

              return (
                <text
                  key={`x-label-${index}`}

                  x={point.x}

                  y={
                    height -
                    15
                  }

                  textAnchor="middle"
                  direction={direction}

                  unicodeBidi="plaintext"
                  className="dashboard-chart__axis-label dashboard-chart__axis-label--x"
                >
                  {formatDateLabel(
                    point.date,
                  )}

                </text>
              );

            },
          )}

        </svg>
      </div>
    </div>
  );

}

function Dashboard() {

  const { t, language } =
    useLanguage();

  const direction =
    String(language || '')
      .toLowerCase()
      .startsWith('ar')
      ? 'rtl'
      : 'ltr';

  const dashboardClassName =
    `owner-dashboard owner-dashboard--${direction}`;

  const {
    user,
    getFeature,
  } = useAuth();

  const navigate =
    useNavigate();

  const [
    data,
    setData,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    refreshing,
    setRefreshing,
  ] = useState(false);

  const [error, setError] =
    useState('');

  const [
    analyticsDays,
    setAnalyticsDays,
  ] = useState(30);

  const isSuperAdminUser =
    useMemo(
      () =>
        isSuperAdminRole(
          user?.role ||
            user?.type ||
            user?.account_type,
        ),
      [user],

    );

  const establishmentId =
    useMemo(
      () => {

        if (isSuperAdminUser) {
          return null;

        }

        return (
          Number(
            localStorage.getItem(
              ESTABLISHMENT_STORAGE_KEY,
            ) || 0,
          ) || null
        );

      },
      [isSuperAdminUser],

    );

  const fetchDashboard =
    useCallback(
      async (
        silent = false,
      ) => {

        if (silent) {
          setRefreshing(true);

        } else {
          setLoading(true);

        }

        setError('');

        try {

          const response =
            await dashboardApi.getDashboard(
              {
                establishment_id:
                  isSuperAdminUser
                    ? undefined
                    : establishmentId ||
                      undefined,
                locale:
                  language,
                days:
                  analyticsDays,
              },
            );

          setData(
            response.data?.data ||
              null,
          );

        } catch (
          requestError
        ) {
          setError(
            requestError
              ?.response
              ?.data
              ?.message ||
              t(
                'dashboard.dynamic.errors.load',
              ),
          );

        } finally {

          if (silent) {
            setRefreshing(false);

          } else {
            setLoading(false);

          }

        }

      },
      [
        analyticsDays,
        establishmentId,
        isSuperAdminUser,
        language,
        t,
      ],

    );

  useEffect(() => {
    fetchDashboard();

  }, [fetchDashboard]);

  const featureAvailable =
    useCallback(
      (key) => {

        if (
          typeof getFeature !==
          'function'
        ) {
          return false;

        }

        const feature =
          getFeature(key);

        return (
          feature?.visible ===
            true &&
          feature?.available ===
            true &&
          feature?.locked !==
            true
        );

      },
      [getFeature],

    );

  if (loading) {

    return (
      <div
        className={dashboardClassName}

        dir={direction}

      >
        <section className="dashboard-loading-state">
          <RefreshCw
            size={30}

            className="dashboard-spin"
          />
          <strong>
            {t(
              'dashboard.dynamic.loading',
            )}

          </strong>
        </section>
      </div>
    );

  }

  if (
    error ||
    !data
  ) {

    return (
      <div
        className={dashboardClassName}

        dir={direction}

      >
        <section className="dashboard-loading-state">
          <strong>
            {t(
              'dashboard.dynamic.errors.title',
            )}

          </strong>
          <p>
            {error}

          </p>
          <button
            type="button"
            className="dashboard-primary-button"
            onClick={() =>
              fetchDashboard()
            }

          >
            <RefreshCw
              size={17}

            />
            {t(
              'common.retry',
            )}

          </button>
        </section>
      </div>
    );

  }

  const dashboardRoleMode =
    normalizeRole(
      data.role_mode,
    );

  const isSuperAdminDashboard =
    isSuperAdminUser ||
    dashboardRoleMode ===
      'superadmin';

  if (
    isSuperAdminDashboard
  ) {

    const statistics = [
      {
        id: 'managers',
        title: t(
          'dashboard.dynamic.superAdmin.managers',
        ),
        value:
          formatNumber(
            data.summary
              ?.managers,
            language,
          ),
        description: t(
          'dashboard.dynamic.superAdmin.managersDescription',
        ),
        icon: Users,
      },
      {
        id: 'establishments',
        title: t(
          'dashboard.dynamic.superAdmin.establishments',
        ),
        value:
          formatNumber(
            data.summary
              ?.establishments,
            language,
          ),
        description: t(
          'dashboard.dynamic.superAdmin.establishmentsDescription',
        ),
        icon: Store,
      },
      {
        id: 'menus',
        title: t(
          'dashboard.dynamic.superAdmin.activeMenus',
        ),
        value:
          formatNumber(
            data.summary
              ?.active_menus,
            language,
          ),
        description: t(
          'dashboard.dynamic.superAdmin.activeMenusDescription',
        ),
        icon:
          UtensilsCrossed,
      },
      {
        id: 'qrOrders',
        title: t(
          'dashboard.dynamic.superAdmin.qrOrders',
        ),
        value:
          formatNumber(
            data.summary
              ?.qr_print_orders,
            language,
          ),
        description: t(
          'dashboard.dynamic.superAdmin.qrOrdersDescription',
        ),
        icon: QrCode,
      },
    ];

    const superAdminActions = [
      {
        id: 'managers',
        title: t(
          'usersManagement.managers.title',
        ),
        description: t(
          'usersManagement.managers.description',
        ),
        symbol: 'U',
        path: '/users',
      },
      {
        id: 'establishments',
        title: t(
          'sidebar.establishments',
        ),
        description: t(
          'dashboard.dynamic.superAdmin.establishmentsDescription',
        ),
        symbol: 'E',
        path: '/establishments',
      },
      {
        id: 'features',
        title: t(
          'sidebar.platformFeatures',
        ),
        description: t(
          'platformFeatures.description',
        ),
        symbol: 'F',
        path: '/platform-features',
      },
      {
        id: 'qrPrintOrders',
        title: t(
          'dashboard.dynamic.superAdmin.qrOrders',
        ),
        description: t(
          'dashboard.dynamic.superAdmin.qrOrdersDescription',
        ),
        symbol: 'QR',
        path: '/qr-print/orders',
      },
    ];

    return (
      <div
        className={dashboardClassName}

        dir={direction}

      >
        <section className="dashboard-page-heading dashboard-page-heading--card">
          <div>
            <p className="dashboard-page-heading__eyebrow">
              {t(
                'dashboard.dynamic.superAdmin.eyebrow',
              )}

            </p>
            <h1>
              {t(
                'dashboard.dynamic.superAdmin.title',
              )}

            </h1>
            <p className="dashboard-page-heading__description">
              {t(
                'dashboard.dynamic.superAdmin.description',
              )}

            </p>
          </div>
          <button
            type="button"
            className="dashboard-secondary-button"
            onClick={() =>
              fetchDashboard(
                true,
              )
            }

          >
            <RefreshCw
              size={17}

              className={
                refreshing
                  ? 'dashboard-spin'
                  : ''
              }

            />
            {t(
              'common.refresh',
            )}

          </button>
        </section>
        <section className="dashboard-stat-grid">
          {statistics.map(
            (statistic) => (
              <StatCard
                key={
                  statistic.id
                }

                {...statistic}

              />
            ),
          )}

        </section>
        <section className="dashboard-panel">
          <div className="dashboard-panel__header">
            <div>
              <h2>
                {t(
                  'dashboard.quickActions',
                )}

              </h2>
              <p>
                {t(
                  'dashboard.quickActionsDescription',
                )}

              </p>
            </div>
          </div>
          <div className="quick-action-grid quick-action-grid--dashboard">
            {superAdminActions.map(
              (action) => (
                <ActionCard
                  key={
                    action.id
                  }

                  {...action}

                />
              ),
            )}

          </div>
        </section>
        <RecentActivity
          activities={
            data.recent_activity ||
            []
          }

          canViewAll={false}

        />
      </div>
    );

  }

  if (data.empty) {

    return (
      <div
        className={dashboardClassName}

        dir={direction}

      >
        <section className="dashboard-loading-state">
          <Store size={34} />
          <strong>
            {t(
              'dashboard.dynamic.empty.title',
            )}

          </strong>
          <p>
            {t(
              'dashboard.dynamic.empty.description',
            )}

          </p>
        </section>
      </div>
    );

  }

  const establishment =
    data.establishment ||
    {};

  const currency =
    establishment.currency ||
    'MAD';

  const summary =
    data.summary || {};

  const trends =
    summary.trends || {};

  const roleMode =
    dashboardRoleMode ||
    'manager';

  const orderToday =
    data.orders?.today || {};

  const isManager =
    roleMode === 'manager';

  const isWaiter =
    roleMode === 'waiter';

  const isKitchen =
    roleMode === 'kitchen';

  const statistics =
    isWaiter
      ? [
          {
            id: 'pending',
            title: t(
              'dashboard.dynamic.orders.pending',
            ),
            value:
              formatNumber(
                orderToday.pending,
                language,
              ),
            description: t(
              'dashboard.dynamic.stats.pendingDescription',
            ),
            icon: Clock3,
          },
          {
            id: 'ready',
            title: t(
              'dashboard.dynamic.orders.ready',
            ),
            value:
              formatNumber(
                orderToday.ready,
                language,
              ),
            description: t(
              'dashboard.dynamic.stats.readyDescription',
            ),
            icon:
              PackageCheck,
          },
          {
            id: 'delivered',
            title: t(
              'dashboard.dynamic.orders.delivered',
            ),
            value:
              formatNumber(
                orderToday.delivered,
                language,
              ),
            description: t(
              'dashboard.dynamic.stats.deliveredDescription',
            ),
            icon:
              ShoppingBag,
          },
          {
            id: 'orders',
            title: t(
              'dashboard.dynamic.stats.todayOrders',
            ),
            value:
              formatNumber(
                summary.today_orders,
                language,
              ),
            description: t(
              'dashboard.dynamic.stats.todayOrdersDescription',
            ),
            icon:
              BarChart3,
          },
        ]
      : isKitchen
        ? [
            {
              id: 'accepted',
              title: t(
                'dashboard.dynamic.orders.accepted',
              ),
              value:
                formatNumber(
                  orderToday.accepted,
                  language,
                ),
              description: t(
                'dashboard.dynamic.stats.acceptedDescription',
              ),
              icon:
                Clock3,
            },
            {
              id: 'preparing',
              title: t(
                'dashboard.dynamic.orders.preparing',
              ),
              value:
                formatNumber(
                  orderToday.preparing,
                  language,
                ),
              description: t(
                'dashboard.dynamic.stats.preparingDescription',
              ),
              icon:
                ChefHat,
            },
            {
              id: 'ready',
              title: t(
                'dashboard.dynamic.orders.ready',
              ),
              value:
                formatNumber(
                  orderToday.ready,
                  language,
                ),
              description: t(
                'dashboard.dynamic.stats.readyDescription',
              ),
              icon:
                PackageCheck,
            },
            {
              id: 'orders',
              title: t(
                'dashboard.dynamic.stats.todayOrders',
              ),
              value:
                formatNumber(
                  summary.today_orders,
                  language,
                ),
              description: t(
                'dashboard.dynamic.stats.todayOrdersDescription',
              ),
              icon:
                BarChart3,
            },
          ]
        : [
            {
              id: 'orders',
              title: t(
                'dashboard.dynamic.stats.todayOrders',
              ),
              value:
                formatNumber(
                  summary.today_orders,
                  language,
                ),
              description: t(
                'dashboard.dynamic.stats.todayOrdersDescription',
              ),
              icon:
                ShoppingBag,
              ...(() => {

                const trend =
                  normalizeTrend(
                    trends.orders,
                  );

                return trend
                  ? {
                      trend:
                        trend.label,
                      trendDirection:
                        trend.direction,
                    }

                  : {};

              })(),
            },
            {
              id: 'revenue',
              title: t(
                'dashboard.dynamic.stats.todayRevenue',
              ),
              value:
                formatMoney(
                  summary.today_revenue,
                  currency,
                  language,
                ),
              description: t(
                'dashboard.dynamic.stats.todayRevenueDescription',
              ),
              icon:
                CircleDollarSign,
              ...(() => {

                const trend =
                  normalizeTrend(
                    trends.revenue,
                  );

                return trend
                  ? {
                      trend:
                        trend.label,
                      trendDirection:
                        trend.direction,
                    }

                  : {};

              })(),
            },
            {
              id: 'views',
              title: t(
                'dashboard.dynamic.stats.menuViews',
                {
                  days:
                    analyticsDays,
                },
              ),
              value:
                formatNumber(
                  summary.menu_views,
                  language,
                ),
              description: t(
                'dashboard.dynamic.stats.menuViewsDescription',
              ),
              icon:
                BarChart3,
              ...(() => {

                const trend =
                  normalizeTrend(
                    trends.views,
                  );

                return trend
                  ? {
                      trend:
                        trend.label,
                      trendDirection:
                        trend.direction,
                    }

                  : {};

              })(),
            },
            {
              id: 'products',
              title: t(
                'dashboard.stats.products',
              ),
              value:
                formatNumber(
                  summary.products,
                  language,
                ),
              description: t(
                'dashboard.dynamic.stats.availableProducts',
                {
                  count:
                    summary.active_products ||
                    0,
                },
              ),
              icon:
                UtensilsCrossed,
            },
          ];

  const quickActions = [
    ...(isManager
      ? [
          {
            id: 'product',
            title: t(
              'dashboard.actions.addProduct',
            ),
            description: t(
              'dashboard.actions.addProductDescription',
            ),
            symbol: '+',
            path: '/products/new',
          },
          {
            id: 'menu',
            title: t(
              'dashboard.dynamic.actions.createMenu',
            ),
            description: t(
              'dashboard.dynamic.actions.createMenuDescription',
            ),
            symbol: 'M',
            path: '/menus/new',
          },
          {
            id: 'orders',
            title: t(
              'dashboard.dynamic.actions.orders',
            ),
            description: t(
              'dashboard.dynamic.actions.ordersDescription',
            ),
            symbol: 'O',
            path: '/orders',
          },
          {
            id: 'analytics',
            title: t(
              'dashboard.actions.viewAnalytics',
            ),
            description: t(
              'dashboard.actions.viewAnalyticsDescription',
            ),
            symbol: '↗',
            path:
              '/menu-analytics',
          },
          {
            id: 'qr',
            title: t(
              'dashboard.dynamic.actions.qrPrint',
            ),
            description: t(
              'dashboard.dynamic.actions.qrPrintDescription',
            ),
            symbol: 'QR',
            path:
              '/qr-print/products',
          },
        ]
      : []),
    ...(isWaiter
      ? [
          {
            id: 'newOrder',
            title: t(
              'dashboard.dynamic.actions.newOrder',
            ),
            description: t(
              'dashboard.dynamic.actions.newOrderDescription',
            ),
            symbol: '+',
            path:
              '/orders/new',
          },
          {
            id: 'orders',
            title: t(
              'dashboard.dynamic.actions.orders',
            ),
            description: t(
              'dashboard.dynamic.actions.ordersDescription',
            ),
            symbol: 'O',
            path: '/orders',
          },
        ]
      : []),
    ...(isKitchen
      ? [
          {
            id: 'orders',
            title: t(
              'dashboard.dynamic.actions.kitchenOrders',
            ),
            description: t(
              'dashboard.dynamic.actions.kitchenOrdersDescription',
            ),
            symbol: 'K',
            path: '/orders',
          },
        ]
      : []),
    ...(isManager &&
    featureAvailable(
      'ai_menu_import',
    )
      ? [
          {
            id: 'aiImport',
            title: t(
              'dashboard.dynamic.actions.aiImport',
            ),
            description: t(
              'dashboard.dynamic.actions.aiImportDescription',
            ),
            symbol: 'AI',
            path: '/menu-import',
          },
        ]
      : []),
    ...(isManager &&
    featureAvailable(
      'ai_menu_design',
    ) &&
    establishment.default_menu?.id
      ? [
          {
            id: 'aiDesign',
            title: t(
              'dashboard.dynamic.actions.aiDesign',
            ),
            description: t(
              'dashboard.dynamic.actions.aiDesignDescription',
            ),
            symbol: 'AI',
            path: `/menus/${establishment.default_menu.id}/ai-design`,
          },
        ]
      : []),
  ];

  const defaultMenu =
    establishment.default_menu;

  return (
    <div
        className={`${dashboardClassName} owner-dashboard--${roleMode}`}

        dir={direction}

      >
      <section className="dashboard-page-heading dashboard-page-heading--card">
        <div>
          <p className="dashboard-page-heading__eyebrow">
            {isManager
              ? t('dashboard.dynamic.manager.eyebrow')
              : isWaiter
                ? t('dashboard.dynamic.waiter.eyebrow')
                : t('dashboard.dynamic.kitchen.eyebrow')}

          </p>
          <h1>
            {isManager
              ? t('dashboard.dynamic.manager.title')
              : isWaiter
                ? t('dashboard.dynamic.waiter.title')
                : t('dashboard.dynamic.kitchen.title')}

          </h1>
          <div className="dashboard-page-heading__context">
            <Store size={16} />
            <span>{t('dashboard.dynamic.establishment.label')}</span>
            <strong>{establishment.name || t('dashboard.title')}</strong>
          </div>
          <p className="dashboard-page-heading__description">
            {isManager
              ? t('dashboard.dynamic.manager.description')
              : isWaiter
                ? t('dashboard.dynamic.waiter.description')
                : t('dashboard.dynamic.kitchen.description')}

          </p>
        </div>
        <div className="dashboard-page-heading__actions">
          {defaultMenu?.slug ? (
            <button
              type="button"
              className="dashboard-secondary-button"
              onClick={() =>
                navigate(
                  `/menu/${defaultMenu.slug}`,
                )
              }

            >
              <Eye size={18} />
              {t(
                'dashboard.previewMenu',
              )}

            </button>
          ) : null}

          {isManager ? (
            <button
              type="button"
              className="dashboard-primary-button"
              onClick={() =>
                navigate(
                  '/products/new',
                )
              }

            >
              <Plus size={18} />
              {t(
                'dashboard.addProduct',
              )}

            </button>
          ) : null}

          <button
            type="button"
            className="dashboard-secondary-button"
            onClick={() =>
              fetchDashboard(
                true,
              )
            }

          >
            <RefreshCw
              size={17}

              className={
                refreshing
                  ? 'dashboard-spin'
                  : ''
              }

            />
            {t(
              'common.refresh',
            )}

          </button>
        </div>
      </section>
      <section className="dashboard-stat-grid">
        {statistics.map(
          (statistic) => (
            <StatCard
              key={
                statistic.id
              }

              {...statistic}

            />
          ),
        )}

      </section>
      {!isManager ? (
      <section className="dashboard-live-strip">
        <article>
          <Tags size={18} />
          <div>
            <span>
              {t(
                'dashboard.dynamic.live.activeCategories',
              )}

            </span>
            <strong>
              {formatNumber(
                summary.active_categories,
                language,
              )}

            </strong>
          </div>
        </article>
        <article>
          <Users size={18} />
          <div>
            <span>
              {t(
                'dashboard.dynamic.live.activeStaff',
              )}

            </span>
            <strong>
              {formatNumber(
                summary.active_staff,
                language,
              )}

            </strong>
          </div>
        </article>
        <article>
          <UtensilsCrossed
            size={18}

          />
          <div>
            <span>
              {t(
                'dashboard.dynamic.live.activeMenus',
              )}

            </span>
            <strong>
              {formatNumber(
                summary.active_menus,
                language,
              )}

            </strong>
          </div>
        </article>
        <Link
          to="/notifications"
          className="dashboard-live-strip__link"
        >
          <Bell size={18} />
          <div>
            <span>
              {t(
                'dashboard.dynamic.live.notifications',
              )}

            </span>
            <strong>
              {formatNumber(
                summary.unread_notifications,
                language,
              )}

            </strong>
          </div>
        </Link>
        <article>
          <ShoppingBag size={18} />
          <div>
            <span>
              {t(
                'dashboard.dynamic.live.ordering',
              )}

            </span>
            <strong>
              {establishment.ordering_enabled
                ? t(
                    'dashboard.dynamic.on',
                  )
                : t(
                    'dashboard.dynamic.off',
                  )}

            </strong>
          </div>
        </article>
      </section>
      ) : null}

      <div className="dashboard-main-grid dashboard-main-grid--balanced dashboard-orders-overview-grid">
        <section className="dashboard-panel">
          <div className="dashboard-panel__header">
            <div>
              <h2>
                {t(
                  'dashboard.dynamic.orders.title',
                )}

              </h2>
              <p>
                {t(
                  'dashboard.dynamic.orders.description',
                )}

              </p>
            </div>
            <Link
              to="/orders"
              className="dashboard-text-link"
            >
              {t(
                'common.viewAll',
              )}

            </Link>
          </div>
          <div className="dashboard-order-status-grid">
            {[
              'pending',
              'accepted',
              'preparing',
              'ready',
              'delivered',
            ].map(
              (status) => (
                <Link
                  key={status}

                  to={`/orders?status=${status}`}

                  className="dashboard-order-status"
                >
                  <span>
                    {t(
                      `dashboard.dynamic.orders.${status}`,
                    )}

                  </span>
                  <strong>
                    {formatNumber(
                      orderToday[
                        status
                      ],

                      language,
                    )}

                  </strong>
                </Link>
              ),
            )}

          </div>
          <div className="dashboard-recent-orders">
            {(data.orders?.recent || []).length ? (
              (data.orders?.recent || []).map((order) => (
              <Link
                to={`/orders/${order.id}`}

                key={order.id}

                className="dashboard-recent-order"
              >
                <div>
                  <strong>
                    #
                    {order.order_number ||
                      order.id}

                  </strong>
                  <span>
                    {order.customer_name ||
                      t(
                        'publicMenu.anonymous',
                      )}

                  </span>
                </div>
                <span className={`dashboard-order-pill dashboard-order-pill--${order.status}`}>
                  {t(
                    `orders.status.${order.status}`,
                  )}

                </span>
                <strong>
                  {formatMoney(
                    order.total_amount,
                    order.currency ||
                      currency,
                    language,
                  )}

                </strong>
              </Link>
            ))
            ) : (
              <div className="dashboard-mini-empty">
                {t('dashboard.dynamic.orders.empty')}

              </div>
            )}

          </div>
        </section>
      </div>
      {isManager ? (
        <section className="dashboard-panel dashboard-analytics-panel">
          <div className="dashboard-panel__header">
            <div>
              <h2>
                {t(
                  'dashboard.dynamic.analytics.title',
                )}

              </h2>
              <p>
                {t(
                  'dashboard.dynamic.analytics.description',
                )}

              </p>
            </div>
            <div className="dashboard-range-toggle">
              {[7, 30].map(
                (days) => (
                  <button
                    key={days}

                    type="button"
                    className={
                      analyticsDays ===
                      days
                        ? 'is-active'
                        : ''
                    }

                    onClick={() =>
                      setAnalyticsDays(
                        days,
                      )
                    }

                  >
                    {t(
                      'dashboard.dynamic.analytics.days',
                      {
                        count:
                          days,
                      },
                    )}

                  </button>
                ),
              )}

            </div>
          </div>
          <div className="dashboard-analytics-grid">
            <DashboardLineChart
              points={
                data.analytics
                  ?.series ||
                []
              }

              label={t(
                'dashboard.dynamic.analytics.chartLabel',
              )}

              language={
                language
              }

              direction={
                direction
              }

              averageLabel={t(
                'dashboard.dynamic.analytics.average',
              )}

              peakLabel={t(
                'dashboard.dynamic.analytics.peak',
              )}

            />
            <div className="dashboard-top-products">
              <div className="dashboard-top-products__heading">
                <strong>
                  {t(
                    'dashboard.dynamic.analytics.topProducts',
                  )}

                </strong>
                <span>
                  {t(
                    'dashboard.dynamic.analytics.byViews',
                  )}

                </span>
              </div>
              {(data.analytics
                ?.top_products ||
                []
              ).length ? (
                data.analytics.top_products.map(
                  (
                    product,
                    index,
                  ) => (
                    <Link
                      key={
                        product.product_id
                      }

                      to={`/products/${product.product_id}`}

                      className="dashboard-top-product"
                    >
                      <span>
                        {index +
                          1}

                      </span>
                      <strong>
                        {
                          product.name
                        }

                      </strong>
                      <small>
                        {t(
                          'dashboard.dynamic.analytics.views',
                          {
                            count:
                              product.visits,
                          },
                        )}

                      </small>
                    </Link>
                  ),
                )
              ) : (
                <div className="dashboard-mini-empty">
                  {t(
                    'dashboard.dynamic.analytics.noProducts',
                  )}

                </div>
              )}

            </div>
          </div>
        </section>
      ) : null}

      {isManager ? (
        <div className="dashboard-main-grid dashboard-main-grid--balanced">
          <section className="dashboard-panel">
            <div className="dashboard-panel__header">
              <div>
                <h2>
                  {t(
                    'dashboard.quickActions',
                  )}

                </h2>
                <p>
                  {t(
                    'dashboard.quickActionsDescription',
                  )}

                </p>
              </div>
            </div>
            <div className="quick-action-grid quick-action-grid--dashboard">
              {quickActions.map(
                (action) => (
                  <ActionCard
                    key={
                      action.id
                    }

                    {...action}

                  />
                ),
              )}

            </div>
          </section>
          <section className="dashboard-panel">
            <div className="dashboard-panel__header">
              <div>
                <h2>
                  {t(
                    'dashboard.dynamic.qrPrint.title',
                  )}

                </h2>
                <p>
                  {t(
                    'dashboard.dynamic.qrPrint.description',
                  )}

                </p>
              </div>
              <Link
                to="/qr-print/orders"
                className="dashboard-text-link"
              >
                {t(
                  'common.viewAll',
                )}

              </Link>
            </div>
            <div className="dashboard-qr-summary">
              {[
                [
                  'cart_items',
                  'cart',
                ],

                [
                  'quotation_requested',
                  'quotes',
                ],

                [
                  'negotiating',
                  'negotiating',
                ],

                [
                  'in_production',
                  'production',
                ],

                [
                  'ready',
                  'ready',
                ],

              ].map(
                ([
                  key,
                  label,
                ]) => (
                  <article
                    key={key}

                  >
                    <span>
                      {t(
                        `dashboard.dynamic.qrPrint.${label}`,
                      )}

                    </span>
                    <strong>
                      {formatNumber(
                        data
                          .qr_print?.[
                          key
                        ],

                        language,
                      )}

                    </strong>
                  </article>
                ),
              )}

            </div>
          </section>
        </div>
      ) : (
        <section className="dashboard-panel">
          <div className="dashboard-panel__header">
            <div>
              <h2>
                {t(
                  'dashboard.quickActions',
                )}

              </h2>
            </div>
          </div>
          <div className="quick-action-grid">
            {quickActions.map(
              (action) => (
                <ActionCard
                  key={
                    action.id
                  }

                  {...action}

                />
              ),
            )}

          </div>
        </section>
      )}

      {isManager ? (
        <section className="dashboard-panel">
          <div className="dashboard-panel__header">
            <div>
              <h2>
                {t(
                  'dashboard.dynamic.menus.title',
                )}

              </h2>
              <p>
                {t(
                  'dashboard.dynamic.menus.description',
                )}

              </p>
            </div>
            <Link
              to="/menus"
              className="dashboard-text-link"
            >
              {t(
                'common.viewAll',
              )}

            </Link>
          </div>
          <div className="dashboard-menu-list">
            {(data.menus || []).length ? (
              (data.menus || []).map((menu) => (
                <article
                  key={menu.id}

                  className="dashboard-menu-row"
                >
                  <div>
                    <strong>
                      {menu.name}

                    </strong>
                    <span>
                      {menu.is_default
                        ? t(
                            'menus.status.default',
                          )
                        : menu.is_active
                          ? t(
                              'menus.status.active',
                            )
                          : t(
                              'menus.status.inactive',
                            )}

                    </span>
                  </div>
                  <div className="dashboard-menu-row__actions">
                    <Link
                      to={`/menus/${menu.id}/customize`}

                    >
                      {t(
                        'menus.actions.customize',
                      )}

                    </Link>
                    {menu.slug &&
                    menu.is_active ? (
                      <Link
                        to={`/menu/${menu.slug}`}

                      >
                        {t(
                          'menus.actions.publicPreview',
                        )}

                      </Link>
                    ) : null}

                  </div>
                </article>
              ))
            ) : (
              <div className="dashboard-mini-empty">
                {t('dashboard.dynamic.menus.empty')}

              </div>
            )}

          </div>
        </section>
      ) : null}

      {isManager ? (
        <RecentActivity
          activities={
            data.recent_activity ||
            []
          }

          canViewAll
        />
      ) : null}

    </div>
  );

}

export default Dashboard;
